/**
 * @file Adaptive quiz engine — pure, React-free CAT mechanics (#1205).
 * @module @labcraft/course-kit/runtime/quiz_engine
 *
 * A lightweight Computerized Adaptive Testing engine (umbrella #1203
 * §"Adaptive engine — how it works"). It keeps CAT's two useful
 * behaviours — difficulty that adapts to demonstrated performance and a
 * variable session length — while replacing the IRT statistical core
 * (a cold-start blocker at launch, per Q2) with a discrete difficulty
 * ladder + deterministic scoring against a **fixed** mastery envelope.
 *
 * Everything here is a pure function over an explicit immutable
 * {@link QuizState}, mirroring the `scanner.ts` / `reducer.ts` shape.
 * The engine is deliberately I/O-free and randomness-free at the
 * boundary: the non-determinism it needs — the draw policy's coin flip
 * and the random within-band item pick — is threaded through an injected
 * `rng: () => number` on {@link QuizConfig}, so tests drive it with a
 * seeded stub and the production caller passes `Math.random`. Every parameter in the
 * umbrella's parameter table is passed-in config (`design.md`:
 * "Defaults belong to the operator"); nothing is baked into a schema
 * default here.
 *
 * The five load-bearing mechanics, each realised by a method on
 * {@link QuizEngine}:
 *
 *   1. Difficulty ladder + item selection — {@link QuizEngine.selectNext}.
 *   2. Weighted scoring vs a fixed `MAX` + prior-wrong discount —
 *      {@link QuizEngine.apply}.
 *   3. Deterministic graders for the three kinds —
 *      {@link QuizEngine.grade}.
 *   4. SPRT-style early stop — {@link QuizEngine.decision}, using the
 *      optimistic {@link QuizEngine.bestCaseRemaining} bound.
 *   5. Draw policy (chapter concept vs an upstream `requires`
 *      predecessor) — folded into {@link QuizEngine.selectNext} via
 *      {@link QuizEngine.drawConcept}.
 *
 * Anti-gaming (Q7) is structural, not a separate guard: reaching `T`
 * requires banking high weights, the ladder auto-ascends on every
 * correct answer (so cheap band-1 credits can't be farmed), and the
 * only way back to an easy question is to answer wrong (earns 0, burns
 * budget, descends the ladder, triggers the prior-wrong discount). A
 * deliberate-fail run is strictly self-defeating and bails on the FAIL
 * boundary. The unit spec proves this as an explicit property.
 */

import type {
  ChapterQuiz,
  Difficulty,
  QuizAnswer,
  QuizQuestion,
} from '../types.ts'
import type { CourseGraph, NodeId } from '../graph_types.ts'

/**
 * The submitted answer for one question, discriminated the same way as
 * {@link QuizAnswer}: an `mc` submission names a chosen option `id`; a
 * `command` / `fill-blank` submission carries the learner's free text.
 * Passed to {@link QuizEngine.grade} against the question's authored
 * answer.
 *
 * @property kind - discriminator; matches the graded question's `kind`
 * @property optionId - `mc` only: the `QuizOption.id` the learner chose
 * @property text - `command` / `fill-blank` only: the free text typed
 */
export type QuizSubmission =
  | { kind: 'mc'; optionId: string }
  | { kind: 'command' | 'fill-blank'; text: string }

/**
 * Operator-pinned engine configuration — every knob from the umbrella's
 * parameter table, supplied at the call site rather than defaulted in a
 * schema (`design.md`: "Defaults belong to the operator"). The caller
 * pins each value (recommended starting values live in the umbrella's
 * table) and threads randomness through `rng`.
 *
 * @property weight - weight of a question of difficulty `d`; the credit
 *   a correct answer at that band banks. Linear (`d => d`) by default at
 *   the call site; an arbitrary function is accepted (e.g. `d => d * d`
 *   for a steeper high-band premium). Must be non-negative and
 *   non-decreasing in `d` for the ladder's anti-gaming property to hold;
 *   the engine does not enforce that (operator policy).
 * @property startBand - the difficulty band the session opens at. The
 *   caller resolves it from `ChapterQuiz.startBand` when the author set
 *   one, else its own operator-pinned default; the engine takes the
 *   resolved value.
 * @property max - the **fixed** per-chapter mastery envelope: the
 *   weighted points a mastery performance banks, used as the scoring
 *   denominator. Load-bearing that this is fixed (not the running
 *   asked-so-far total) — see the file header's anti-gaming note.
 * @property passThreshold - the pass fraction; the engine derives the
 *   absolute bar `T = passThreshold * max`.
 * @property minQuestions - the floor of questions asked before a
 *   provisional PASS may fire, so a lucky top-band start cannot
 *   insta-pass.
 * @property maxQuestions - the hard cap; at this many asked the engine
 *   forces a decision (PASS iff `T` reached, else FAIL).
 * @property priorWrongDecay - the per-prior-wrong multiplier applied to
 *   a concept's earned credit: `credit *= priorWrongDecay ^ priorWrongs`.
 *   A value in `(0, 1]` discounts a stumble; `1` disables the discount.
 * @property interleaveRatio - the probability a draw targets an upstream
 *   `requires` predecessor of the chapter concept rather than the
 *   chapter concept itself. `0` never interleaves; `1` always does.
 * @property rng - the randomness source, `() => number` in `[0, 1)`.
 *   Production passes `Math.random`; tests pass a deterministic stub.
 *   The only non-determinism the engine consumes.
 */
export interface QuizConfig {
  weight: (d: Difficulty) => number
  startBand: Difficulty
  max: number
  passThreshold: number
  minQuestions: number
  maxQuestions: number
  priorWrongDecay: number
  interleaveRatio: number
  rng: () => number
}

/**
 * The terminal / continue verdict from {@link QuizEngine.decision}
 * (umbrella #1203 §5, the SPRT-style stop).
 *
 *   - `pass` — `earned >= T`, the accept boundary (gated by
 *     `minQuestions`), or the forced PASS at `maxQuestions`.
 *   - `fail` — even a best-case remaining run cannot reach `T`, the
 *     reject boundary, or the forced FAIL at `maxQuestions`.
 *   - `continue` — neither boundary crossed; ask another question.
 */
export type QuizDecision = 'pass' | 'fail' | 'continue'

/**
 * The engine's immutable state, threaded through the pure step methods
 * (mirrors `reducer.ts`'s `ChapterState`). Every step returns a fresh
 * object so a consuming reducer's reference-equality bailouts work; the
 * bank, graph, and config are carried by reference (read-only inputs).
 *
 * @property config - the operator-pinned {@link QuizConfig}
 * @property bank - the chapter's authored question pool, drawn from by
 *   concept + band. Interleaved questions targeting upstream
 *   predecessors are expected to be present in this pool too (the loaded
 *   manifest merges prior chapters' banks upstream of the engine).
 * @property graph - the composed course-graph region used to resolve a
 *   concept's upstream `requires` predecessors for the draw policy
 * @property concepts - the non-empty concept SET the draw rotates across
 *   (umbrella #1357; was the single `chapterConcept` under #1203). The
 *   draw picks a base concept from this set, then with probability
 *   `interleaveRatio` interleaves that base concept's upstream `requires`
 *   predecessors. A singleton set reproduces the pre-#1357 single-concept
 *   behaviour exactly.
 * @property band - the current difficulty band (1–5); starts at
 *   `config.startBand`, steps `min(band+1, 5)` on correct and
 *   `max(band-1, 1)` on wrong
 * @property asked - the count of questions asked so far (the ids in
 *   `usedIds`); the budget floor/cap axis
 * @property earned - the weighted points banked so far (post-discount);
 *   monotonically non-decreasing
 * @property usedIds - ids of questions already served, so the same item
 *   is never drawn twice
 * @property priorWrongs - per-concept count of wrong answers, keyed by
 *   `NodeId`, driving the prior-wrong discount on that concept's future
 *   credit
 */
export interface QuizState {
  config: QuizConfig
  bank: QuizQuestion[]
  graph: CourseGraph
  concepts: NodeId[]
  band: Difficulty
  asked: number
  earned: number
  usedIds: ReadonlySet<string>
  priorWrongs: ReadonlyMap<NodeId, number>
}

/**
 * The pure adaptive quiz engine. A namespace of `static` step functions
 * over an explicit {@link QuizState} — no instance state, no I/O, no
 * React — so each mechanic is individually unit-testable and the whole
 * composes into Part C's runtime phase machine. Statics per
 * `typescript.md` §"Functions belong on classes": these are the
 * vocabulary of the quiz concept, tied to no instance.
 *
 * Typical drive loop (Part C wires this to the reducer / UI):
 *
 * ```ts
 * let s = QuizEngine.init(config, quiz, graph, [concept])
 * while (QuizEngine.decision(s) === 'continue') {
 *   const q = QuizEngine.selectNext(s)
 *   if (!q) break // bank exhausted
 *   const correct = QuizEngine.grade(q, submission)
 *   s = QuizEngine.apply(s, q, correct)
 * }
 * const passed = QuizEngine.decision(s) === 'pass'
 * ```
 */
export class QuizEngine {
  /**
   * Build the initial {@link QuizState} for a chapter's quiz. The band
   * opens at `config.startBand` (the caller having already resolved
   * `ChapterQuiz.startBand` vs its operator default into that field);
   * nothing is asked or earned yet.
   *
   * @param config - the operator-pinned engine configuration
   * @param quiz - the chapter's authored quiz (its `questions` bank)
   * @param graph - the composed course-graph region for predecessor
   *   resolution
   * @param concepts - the non-empty concept set the draw rotates across
   *   (a single-element array for a per-chapter quiz)
   * @returns the fresh state
   */
  static init(
    config: QuizConfig,
    quiz: ChapterQuiz,
    graph: CourseGraph,
    concepts: NodeId[],
  ): QuizState {
    return {
      config,
      bank: quiz.questions,
      graph,
      concepts,
      band: config.startBand,
      asked: 0,
      earned: 0,
      usedIds: new Set(),
      priorWrongs: new Map(),
    }
  }

  /**
   * Select the next unused question for the current draw. Picks the
   * target concept via the draw policy ({@link QuizEngine.drawConcept}),
   * then an unused item at the current band tagged with that concept;
   * if that exact `(concept, band)` cell is exhausted, falls to the
   * nearest band (by absolute distance, ties preferring the lower band)
   * that still has an unused item for the concept. Returns `null` only
   * when the concept has no unused item at any band — the caller treats
   * that as bank exhaustion for the draw.
   *
   * Within the chosen `(concept, band)` cell the specific item is picked
   * **at random** via `config.rng()`, so two learners on the same
   * answer-path see that band's questions in different orders rather than
   * the fixed authoring order (#1201 — deterministic order let every
   * learner meet the same questions in the same sequence). A seeded `rng`
   * still makes the whole walk reproducible: the pick is a pure function
   * of the rng stream. The rng is consumed only when the cell holds more
   * than one unused item, so a single-candidate draw is choice-free.
   *
   * @param state - the current state
   * @returns the drawn question, or `null` if none is available
   */
  static selectNext(state: QuizState): QuizQuestion | null {
    const concept = QuizEngine.drawConcept(state)
    const forConcept = state.bank.filter(
      q => q.concept === concept && !state.usedIds.has(q.id),
    )
    if (forConcept.length === 0) return null

    const bands = [...new Set(forConcept.map(q => q.difficulty))].sort((a, b) =>
      QuizEngine.bandDistance(a, b, state.band),
    )
    const targetBand = bands[0]!
    const candidates = forConcept.filter(q => q.difficulty === targetBand)
    if (candidates.length === 1) return candidates[0]!
    return candidates[Math.floor(state.config.rng() * candidates.length)]!
  }

  /**
   * Grade a submission against a question's authored answer, purely and
   * deterministically (umbrella #1203 Q1 — no LLM in the gating path).
   *
   *   - `mc` — `submission.optionId === answer.options[0].id` (the
   *     first authored option is the correct answer by convention; the
   *     engine grades the **unshuffled** authored question, so the
   *     reader's display shuffling never mis-grades).
   *   - `command` / `fill-blank` — normalize the submitted text
   *     ({@link QuizEngine.normalize} for `fill-blank`,
   *     {@link QuizEngine.normalizeCommand} for `command`) then match
   *     any-of `accept`: a `string` entry matches when it normalizes to
   *     the same string; a `RegExp` entry matches when it tests true
   *     against the normalized input.
   *
   * A `kind` mismatch between the submission and the question (an `mc`
   * submission for a `command` question, say) grades `false` rather than
   * throwing — a defensive floor; the UI pairs the renderer to the kind,
   * so a mismatch is a programming error the grader refuses to reward.
   *
   * @param question - the question being answered
   * @param submission - the learner's submission
   * @returns whether the submission is correct
   */
  static grade(question: QuizQuestion, submission: QuizSubmission): boolean {
    const answer: QuizAnswer = question.answer
    if (answer.kind === 'mc') {
      // `options[0]` is the correct answer by convention; the engine
      // grades the unshuffled authored question, so the reader's
      // display shuffling never mis-grades.
      return submission.kind === 'mc'
        ? submission.optionId === answer.options[0]?.id
        : false
    }

    if (submission.kind === 'mc') return false
    const isCommand = question.kind === 'command'
    const normalized = isCommand
      ? QuizEngine.normalizeCommand(submission.text)
      : QuizEngine.normalize(submission.text)
    return answer.accept.some(entry =>
      typeof entry === 'string'
        ? QuizEngine.normalizeAccept(isCommand, entry) === normalized
        : entry.test(normalized),
    )
  }

  /**
   * Apply a graded answer, stepping the ladder and accruing score
   * (umbrella #1203 §1–4). Returns a fresh state:
   *
   *   - the band steps `min(band+1, 5)` on correct, `max(band-1, 1)` on
   *     wrong;
   *   - `asked` increments and the question id joins `usedIds`;
   *   - on a **correct** answer, `earned` grows by
   *     `weight(question.difficulty) * priorWrongDecay ^ priorWrongsOn(concept)`
   *     (the prior-wrong discount; never negative because `weight` and
   *     the decay power are non-negative);
   *   - on a **wrong** answer, `earned` is unchanged and the concept's
   *     `priorWrongs` count increments, so a later correct answer on the
   *     same concept banks the discounted credit.
   *
   * @param state - the current state
   * @param question - the question just answered
   * @param correct - whether {@link QuizEngine.grade} returned true
   * @returns the next state
   */
  static apply(
    state: QuizState,
    question: QuizQuestion,
    correct: boolean,
  ): QuizState {
    const band: Difficulty = correct
      ? QuizEngine.stepUp(state.band)
      : QuizEngine.stepDown(state.band)

    const usedIds = new Set(state.usedIds)
    usedIds.add(question.id)

    const priorWrongs = new Map(state.priorWrongs)
    let earned = state.earned
    if (correct) {
      const priors = state.priorWrongs.get(question.concept) ?? 0
      const credit =
        state.config.weight(question.difficulty) *
        state.config.priorWrongDecay ** priors
      earned += Math.max(0, credit)
    } else {
      const priors = state.priorWrongs.get(question.concept) ?? 0
      priorWrongs.set(question.concept, priors + 1)
    }

    return {
      ...state,
      band,
      asked: state.asked + 1,
      earned,
      usedIds,
      priorWrongs,
    }
  }

  /**
   * The SPRT-style stop verdict (umbrella #1203 §5). Evaluated after
   * every {@link QuizEngine.apply}:
   *
   *   - **PASS (accept)** — `earned >= T` and `asked >= minQuestions`.
   *     Since `earned` only grows, the outcome is settled; the
   *     `minQuestions` gate stops a lucky top-band start from
   *     insta-passing.
   *   - **FAIL (reject)** — `earned + bestCaseRemaining < T`: even a
   *     perfect remaining run cannot reach the bar.
   *   - **Forced decision** — at `asked >= maxQuestions`, PASS iff
   *     `earned >= T`, else FAIL.
   *   - **CONTINUE** — otherwise.
   *
   * The forced decision at `maxQuestions` is checked first so the cap
   * always terminates; the `earned >= T` accept is gated by
   * `minQuestions`, so a session that has banked enough but is still
   * under the floor reads `continue` (keep asking) rather than a
   * premature pass.
   *
   * @param state - the current state
   * @returns the verdict
   */
  static decision(state: QuizState): QuizDecision {
    const t = QuizEngine.threshold(state.config)

    if (state.asked >= state.config.maxQuestions) {
      return state.earned >= t ? 'pass' : 'fail'
    }
    if (state.earned >= t && state.asked >= state.config.minQuestions) {
      return 'pass'
    }
    if (state.earned + QuizEngine.bestCaseRemaining(state) < t) {
      return 'fail'
    }
    return 'continue'
  }

  /**
   * The maximum weighted credit still earnable, an **optimistic** upper
   * bound used only by the FAIL boundary (umbrella #1203 §5). A perfect
   * run climbs one band per remaining question from the current band
   * (capped at 5); this sums `weight` over that ascent. It ignores the
   * prior-wrong discount and any bank-exhaustion limit — deliberately,
   * because an over-estimate keeps the FAIL boundary conservative: the
   * engine only rejects when even this best case falls short, so it
   * never fails a run that could still (in principle) pass.
   *
   * The current band is the ladder position **after** the last applied
   * answer, so a wrong answer that dropped the band already shortened
   * this bound — which is exactly why deliberate-failing bails early.
   *
   * @param state - the current state
   * @returns the optimistic best-case remaining credit
   */
  static bestCaseRemaining(state: QuizState): number {
    const remaining = state.config.maxQuestions - state.asked
    let band = state.band
    let sum = 0
    for (let i = 0; i < remaining; i++) {
      sum += state.config.weight(band)
      band = QuizEngine.stepUp(band)
    }
    return sum
  }

  /**
   * The absolute pass bar `T = passThreshold * max`. Factored out so the
   * `decision` / `bestCaseRemaining` boundaries and any caller share one
   * derivation of the threshold from the fixed envelope.
   *
   * @param config - the engine configuration
   * @returns the absolute weighted-points bar
   */
  static threshold(config: QuizConfig): number {
    return config.passThreshold * config.max
  }

  /**
   * Draw policy target-concept pick (umbrella #1203 §6, generalized to a
   * concept SET by umbrella #1357). First picks a **base** concept from
   * `state.concepts`: a singleton set short-circuits to its one entry,
   * consuming NO `rng()` so a single-concept draw stays bit-identical to
   * the pre-#1357 rng stream; a larger set picks one member (uniformly,
   * via {@link QuizEngine.pickWeighted} with equal distances) using one
   * `rng()`. Then, with probability `interleaveRatio`, the draw targets an
   * upstream `requires` predecessor of THAT base concept (nearer
   * predecessors weighted higher) — but only among predecessors the loaded
   * bank can actually serve ({@link QuizEngine.hasUnusedItems}); otherwise,
   * when the base concept has no bank-covered predecessor, it targets the
   * base concept itself. Rotating across the set directly is what lets a
   * Part assessment cover sibling concepts that have no `requires` edges
   * between them.
   *
   * @param state - the current state
   * @returns the concept id the next item should be drawn from
   */
  static drawConcept(state: QuizState): NodeId {
    const base =
      state.concepts.length === 1
        ? state.concepts[0]!
        : QuizEngine.pickWeighted(
            state.concepts.map(id => ({ id, distance: 1 })),
            state.config.rng(),
          )
    if (state.config.rng() >= state.config.interleaveRatio) return base
    // Interleave only to predecessors the bank can serve. A Part assessment
    // loads only its own Part's bank, so a base concept's upstream `requires`
    // predecessors (which live in earlier Parts/courses) are typically
    // bankless; targeting one would starve `selectNext` into a null draw and
    // dead-end the learner. Filter to bank-covered predecessors, else fall
    // back to the base concept.
    const predecessors = QuizEngine.upstreamPredecessors(
      state.graph,
      base,
    ).filter(p => QuizEngine.hasUnusedItems(state, p.id))
    if (predecessors.length === 0) return base
    return QuizEngine.pickWeighted(predecessors, state.config.rng())
  }

  /**
   * Whether the loaded bank still holds an unused item tagged with
   * `concept`. The interleave draw ({@link QuizEngine.drawConcept}) uses this
   * to skip upstream predecessors that carry no question in the bank, so an
   * interleave to a bankless predecessor never starves the draw.
   *
   * @param state - the current state (its `bank` + `usedIds`)
   * @param concept - the concept to probe for a servable item
   * @returns `true` iff an unused item with that concept remains
   */
  private static hasUnusedItems(state: QuizState, concept: NodeId): boolean {
    return state.bank.some(
      q => q.concept === concept && !state.usedIds.has(q.id),
    )
  }

  /**
   * The upstream `requires` predecessors of `concept`, each tagged with
   * its hop distance (1 = a direct prerequisite, 2 = a prerequisite of a
   * prerequisite, …). Resolved by a breadth-first walk **up** the
   * `requires` edges (following `to === concept` backwards to `from`) of
   * the course-graph region; `adjacent` and `requiresFor` edges are not
   * prerequisites and are excluded. Nearest distance wins if a node is
   * reachable by multiple paths. The result feeds
   * {@link QuizEngine.pickWeighted}, which weights nearer predecessors
   * higher.
   *
   * @param graph - the course-graph region to walk
   * @param concept - the concept whose predecessors to resolve
   * @returns predecessors with their minimal hop distance, in BFS order
   */
  static upstreamPredecessors(
    graph: CourseGraph,
    concept: NodeId,
  ): { id: NodeId; distance: number }[] {
    const parents = new Map<NodeId, NodeId[]>()
    for (const edge of graph.edges) {
      if (edge.kind !== 'requires') continue
      const list = parents.get(edge.to) ?? []
      list.push(edge.from)
      parents.set(edge.to, list)
    }

    const result: { id: NodeId; distance: number }[] = []
    const seen = new Set<NodeId>([concept])
    let frontier: NodeId[] = [concept]
    let distance = 0
    while (frontier.length > 0) {
      distance++
      const next: NodeId[] = []
      for (const node of frontier) {
        for (const parent of parents.get(node) ?? []) {
          if (seen.has(parent)) continue
          seen.add(parent)
          result.push({ id: parent, distance })
          next.push(parent)
        }
      }
      frontier = next
    }
    return result
  }

  /**
   * Pick one predecessor weighted by inverse hop distance (nearer =
   * higher probability, umbrella #1203 §6): a predecessor at distance
   * `d` gets weight `1 / d`. Splits the unit interval into weight-sized
   * bands and selects by `r` (a single `rng()` sample), so the choice is
   * deterministic given `r`. Assumes a non-empty list (the caller guards
   * emptiness).
   *
   * @param predecessors - candidate predecessors with hop distances
   * @param r - the rng sample in `[0, 1)` selecting the band
   * @returns the chosen predecessor's id
   */
  static pickWeighted(
    predecessors: { id: NodeId; distance: number }[],
    r: number,
  ): NodeId {
    const weights = predecessors.map(p => 1 / p.distance)
    const total = weights.reduce((a, b) => a + b, 0)
    let cursor = r * total
    for (let i = 0; i < predecessors.length; i++) {
      cursor -= weights[i]!
      if (cursor < 0) return predecessors[i]!.id
    }
    return predecessors[predecessors.length - 1]!.id
  }

  /**
   * Step the band up one on a correct answer, capped at 5. `Difficulty`
   * is a closed `1..5` union, so the cap keeps the return in-type.
   *
   * @param band - the current band
   * @returns `min(band + 1, 5)`
   */
  static stepUp(band: Difficulty): Difficulty {
    return Math.min(band + 1, 5) as Difficulty
  }

  /**
   * Step the band down one on a wrong answer, floored at 1.
   *
   * @param band - the current band
   * @returns `max(band - 1, 1)`
   */
  static stepDown(band: Difficulty): Difficulty {
    return Math.max(band - 1, 1) as Difficulty
  }

  /**
   * Absolute distance between a candidate band and the target band, used
   * as the sort comparator when the exact target band is exhausted so
   * the nearest band wins. Ties (a band above and below equidistant)
   * prefer the **lower** band — an easier fallback is the safer default
   * when the ladder's target rung has no item.
   *
   * @param a - first candidate band (sort left)
   * @param b - second candidate band (sort right)
   * @param target - the current band the ladder points at
   * @returns a negative / zero / positive sort comparator value
   */
  static bandDistance(
    a: Difficulty,
    b: Difficulty,
    target: Difficulty,
  ): number {
    const da = Math.abs(a - target)
    const db = Math.abs(b - target)
    return da === db ? a - b : da - db
  }

  /**
   * Normalize a free-text submission for `fill-blank` grading (umbrella
   * #1203 Decision F). The conservative rule shared by both free-text
   * kinds: trim, then collapse every run of internal whitespace to a
   * single space. This makes leading / trailing / doubled spaces
   * irrelevant without reordering or dropping tokens — a `fill-blank`
   * answer is prose-shaped, so token reordering would change meaning and
   * is **not** applied here (that leniency is `command`-only, see
   * {@link QuizEngine.normalizeCommand}). Case is preserved — a
   * case-insensitive family is expressed by the author as a `RegExp`
   * `accept` entry rather than folded globally.
   *
   * @param text - the raw submitted text
   * @returns the whitespace-normalized string
   */
  static normalize(text: string): string {
    return text.trim().replace(/\s+/g, ' ')
  }

  /**
   * Normalize a free-text submission for `command` grading (umbrella
   * #1203 Decision F). Canonicalizes the variation that does **not**
   * change what a command does while preserving every token that does —
   * the grader must never see `cat > f` and `cat f` as the same command
   * (courses/README.md § Adaptive quizzes rule 5). A purpose-built
   * scanner replaces the earlier `shell-quote` `parse` pass, which
   * dropped operator tokens (`cat > f` ≡ `cat f`, `a && b` ≡ `a b`),
   * read a mid-word `#` as a comment (mangling modern `nixpkgs#pkg`
   * refs), and substituted `$?` away. The canonical form:
   *
   *   - **Whitespace** — runs collapse; tokens re-join with single
   *     spaces, so `ls   -la` and `ls -la` normalize equal.
   *   - **Operators** (`|`, `||`, `&&`, `;`, `&`, `>`, `>>`, `<`, `<<`,
   *     `>&`, `|&`, `&>`, …) — preserved as standalone space-separated
   *     tokens: `a|b`, `a | b`, and `a  |  b` all normalize to `a | b`,
   *     which never equals `a b`.
   *   - **Redirection fds** — an unquoted all-digit word glued to a
   *     redirection stays fused to it (`2>`, `2>>`, and fd-duplicating
   *     `2>&1` as one token), with the target a separate word:
   *     `ls /nix 2>/dev/null` ⇢ `ls /nix 2> /dev/null`, never equal to
   *     the three-word `ls /nix 2 > /dev/null` (a spaced `2` is an
   *     argument, not an fd — POSIX quoting/adjacency rules).
   *   - **Comments** — a `#` opening a word starts a comment, preserved
   *     as `# <body>` (body whitespace-collapsed); a mid-word `#` is a
   *     literal character, so `nix shell nixpkgs#jq --command jq
   *     --version` round-trips exactly.
   *   - **Quotes** — stripped only where meaning-neutral
   *     ({@link QuizEngine.canonicalizeQuoted}): `echo "hi"` ⇢
   *     `echo hi`, while content the shell would treat differently
   *     unquoted (whitespace, globs, expansions, a leading `#`/`~`)
   *     stays quoted, canonicalized to `'…'` when the quote style
   *     itself carries no meaning.
   *   - **Everything else** (`$?`, `$(…)`, globs, backslash escapes) —
   *     preserved verbatim.
   *
   * Argument **order** is preserved (`ls -la` ≠ `ls -al` — flag order
   * can be semantically load-bearing, and an author who wants to accept
   * both lists both). The scanner is total: malformed input (an
   * unterminated quote) is consumed leniently to end of input, never
   * thrown on. String `accept` entries pass through this same function
   * ({@link QuizEngine.normalizeAccept}) and `RegExp` entries test
   * against this canonical form — a regex over an operator command must
   * expect the canonical spacing (`a \| b`, `2> \/dev\/null`).
   *
   * @param text - the raw submitted command line
   * @returns the canonical space-joined token form
   */
  static normalizeCommand(text: string): string {
    const tokens: string[] = []

    // The word under assembly: `null` when no word is open. `rawDigits`
    // tracks whether every character so far arrived as an unquoted
    // digit — what qualifies the word as a redirection fd (`2>`).
    let word: string | null = null
    let rawDigits = true

    const flush = (): void => {
      if (word !== null) tokens.push(word === '' ? "''" : word)
      word = null
      rawDigits = true
    }

    let i = 0
    while (i < text.length) {
      const c = text[i]!

      if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
        flush()
        i += 1
        continue
      }

      // A `#` opening a word starts a comment (bash's rule); a mid-word
      // `#`, as in `nixpkgs#jq`, falls through as a literal character.
      if (c === '#' && word === null) {
        const nl = text.indexOf('\n', i)
        const end = nl === -1 ? text.length : nl
        const body = QuizEngine.normalize(text.slice(i + 1, end))
        tokens.push(body === '' ? '#' : `# ${body}`)
        i = end
        continue
      }

      if (c === "'" || c === '"') {
        const quoted = QuizEngine.scanQuoted(text, i)
        word =
          (word ?? '') +
          QuizEngine.canonicalizeQuoted(quoted.content, c, word === null)
        rawDigits = false
        i = quoted.next
        continue
      }

      if (c === '|' || c === '&' || c === ';' || c === '<' || c === '>') {
        const op = QuizEngine.scanOperator(text, i)
        i += op.length

        // An unquoted all-digit word glued to a redirection is its fd
        // (`2>`, `2>>`); keep them fused so `2>/dev/null` never equals
        // the three-word `2 > /dev/null`.
        let token = op
        if (
          (op[0] === '>' || op[0] === '<') &&
          word !== null &&
          word !== '' &&
          rawDigits
        ) {
          token = word + op
          word = null
          rawDigits = true
        } else {
          flush()
        }

        // `>&` / `<&` duplicate an fd; an adjacent digit run (or `-`,
        // the close-fd form) glues on too, so `2>&1` stays one token.
        if (token.endsWith('>&') || token.endsWith('<&')) {
          let j = i
          if (text[j] === '-') j += 1
          else
            while (j < text.length && text[j]! >= '0' && text[j]! <= '9') j += 1
          token += text.slice(i, j)
          i = j
        }

        tokens.push(token)
        continue
      }

      // A backslash escape is preserved verbatim (both characters), so
      // an escaped space stays inside its word and never re-splits.
      if (c === '\\' && i + 1 < text.length) {
        word = (word ?? '') + text.slice(i, i + 2)
        rawDigits = false
        i += 2
        continue
      }

      word = (word ?? '') + c
      if (c < '0' || c > '9') rawDigits = false
      i += 1
    }

    flush()
    return tokens.join(' ')
  }

  /**
   * Scan one quoted segment for {@link QuizEngine.normalizeCommand},
   * starting at the opening quote character. Single quotes run to the
   * next single quote; double quotes honour `\"` / `\\` escapes (kept
   * verbatim in the content so a kept-quoted segment re-renders exactly
   * as typed). An unterminated quote consumes leniently to end of input
   * rather than throwing, keeping the normalizer total.
   *
   * @param text - the full command line
   * @param start - the index of the opening quote character
   * @returns the raw inner content and the index just past the segment
   */
  static scanQuoted(
    text: string,
    start: number,
  ): { content: string; next: number } {
    const quote = text[start]!
    let content = ''
    let i = start + 1
    while (i < text.length) {
      const c = text[i]!
      if (
        quote === '"' &&
        c === '\\' &&
        (text[i + 1] === '"' || text[i + 1] === '\\')
      ) {
        content += text.slice(i, i + 2)
        i += 2
        continue
      }
      if (c === quote) return { content, next: i + 1 }
      content += c
      i += 1
    }
    return { content, next: i }
  }

  /**
   * Scan one operator token for {@link QuizEngine.normalizeCommand},
   * greedy longest-first so `&&` never reads as two `&`s: the
   * three-character forms (`&>>`, `<<<`), then the two-character forms
   * (`&&`, `||`, `>>`, `<<`, `>&`, `<&`, `|&`, `;;`, `&>`), then the
   * single character. The caller has already established `text[start]`
   * is an operator character (`|`, `&`, `;`, `<`, `>`).
   *
   * @param text - the full command line
   * @param start - the index of the first operator character
   * @returns the matched operator string
   */
  static scanOperator(text: string, start: number): string {
    const three = text.slice(start, start + 3)
    if (three === '&>>' || three === '<<<') return three

    const two = text.slice(start, start + 2)
    if (
      two === '&&' ||
      two === '||' ||
      two === '>>' ||
      two === '<<' ||
      two === '>&' ||
      two === '<&' ||
      two === '|&' ||
      two === ';;' ||
      two === '&>'
    ) {
      return two
    }
    return text[start]!
  }

  /**
   * Canonicalize one quoted segment's contribution to its word for
   * {@link QuizEngine.normalizeCommand}. Quotes are dropped **only**
   * when the shell would treat the bare spelling identically: content
   * made purely of plain word characters, and not a word-leading `#` or
   * `~` (bare, those would start a comment / tilde-expand). Everything
   * else keeps its quotes — canonicalized to single quotes when the
   * quote style is meaning-neutral (no `$`, backtick, `\`, `!`, or `'`
   * in the content, so single- and double-quoting are interchangeable),
   * else kept as typed so `"$?"` and `'$?'` stay distinct. An empty
   * segment contributes nothing; a wholly-empty word renders as `''`
   * at flush, so `echo ""` ≡ `echo ''` but never ≡ `echo`.
   *
   * @param content - the segment's raw inner content
   * @param quote - the quote character as typed (`'` or `"`)
   * @param atWordStart - whether the segment opens its word
   * @returns the segment's canonical contribution to the word
   */
  static canonicalizeQuoted(
    content: string,
    quote: string,
    atWordStart: boolean,
  ): string {
    const bare =
      /^[A-Za-z0-9@%_+=:,./^~#-]+$/.test(content) &&
      !(atWordStart && (content.startsWith('#') || content.startsWith('~')))
    if (bare) return content
    if (content === '') return ''

    const neutralStyle = !/[$`\\!']/.test(content)
    if (neutralStyle) return `'${content}'`
    return `${quote}${content}${quote}`
  }

  /**
   * Normalize a **string** `accept` entry with the same rule as the
   * submission it will be compared against, so an author who writes
   * `"ls  -la"` (or a command with quotes) grades equal to a learner's
   * canonical form. Routes to {@link QuizEngine.normalizeCommand} for
   * `command` questions and {@link QuizEngine.normalize} for
   * `fill-blank`, keeping the accept side and the submission side on one
   * normalization so the exact-string match is symmetric.
   *
   * Takes a boolean `isCommand` rather than the kind string because the
   * caller has already discriminated the free-text kind (a `command`
   * question vs. a `fill-blank` one) off the question; the boolean keeps
   * this helper from re-deriving — and re-narrowing — that fact.
   *
   * @param isCommand - true for a `command` question, false for
   *   `fill-blank`
   * @param entry - the authored exact-string accept entry
   * @returns the normalized accept string
   */
  static normalizeAccept(isCommand: boolean, entry: string): string {
    return isCommand
      ? QuizEngine.normalizeCommand(entry)
      : QuizEngine.normalize(entry)
  }
}
